import React from 'react';
import moment from 'moment';
import styled from 'styled-components';

import { Contact, Divider, theme } from 'components';

import { PopulatedUser } from 'api/UserController/getProfile';
import {
  formatPhoneNumber,
  formatPhoneNumberAsLink,
  getUserAddress,
} from 'utils';
import Row from './Row';

import { Container, Section, TouchableValue, Value } from './common';
import { ContactDrawerType } from '../../types';

const StyledContainer = styled(Container)`
  span {
    color: ${theme.gray8};
  }
`;

interface UserDetailsProps {
  contact: PopulatedUser;
  goToContact: (type: ContactDrawerType, contactId: string) => void;
}

const UserDetails: React.FC<UserDetailsProps> = ({ contact, goToContact }) => {
  const contactAddress = getUserAddress(contact);

  const displayForwardSection =
    contact.frontEndInstructions?.forwardCalls?.enabled ||
    contact.frontEndInstructions?.forwardReps?.length > 0;

  return (
    <StyledContainer>
      <Section data-cy="contact-details-contactMethod">
        <Row label="Email">
          <TouchableValue href={`mailto:${contact.email}`}>
            {contact.email}
          </TouchableValue>
        </Row>
        {contact.phone && (
          <Row label="Mobile">
            <TouchableValue href={formatPhoneNumberAsLink(contact.phone)}>
              {formatPhoneNumber(contact.phone)}
            </TouchableValue>
            {!contact?.frontEndInstructions?.releaseCell && (
              <Value style={{ color: theme.gray7 }}>(Do Not Release)</Value>
            )}
          </Row>
        )}
        <Row label="Office">
          <TouchableValue
            href={
              contactAddress.phone !== null
                ? formatPhoneNumberAsLink(contactAddress.phone!)
                : ''
            }
          >
            {formatPhoneNumber(contactAddress.phone)}
          </TouchableValue>
        </Row>
        {contact.officeExtension && (
          <Row label="Ext.">
            <TouchableValue
              href={
                contactAddress.phone !== null
                  ? formatPhoneNumberAsLink(
                      contactAddress.phone!,
                      contact.officeExtension,
                    )
                  : ''
              }
            >
              {contact.officeExtension}
            </TouchableValue>
          </Row>
        )}
      </Section>
      <Divider />
      {displayForwardSection && (
        <>
          <Section data-cy="contacts-details-fd-forward">
            {contact.frontEndInstructions?.forwardReps?.length > 0 ? (
              <Row label="Fwd Msgs">
                {contact.frontEndInstructions.forwardReps.map(rep => (
                  <Contact
                    key={rep.id}
                    contact={rep}
                    loading={false}
                    onClick={() => goToContact(ContactDrawerType.User, rep.id)}
                  />
                ))}
              </Row>
            ) : null}

            {contact.frontEndInstructions?.forwardCalls?.enabled ? (
              <Row label="Fwd Calls">
                <Value>{contact.frontEndInstructions.forwardCalls.name}</Value>
                <TouchableValue
                  href={formatPhoneNumberAsLink(
                    contact.frontEndInstructions.forwardCalls.phone,
                  )}
                >
                  {formatPhoneNumber(
                    contact.frontEndInstructions.forwardCalls.phone,
                  )}
                </TouchableValue>
              </Row>
            ) : null}
          </Section>
          <Divider />
        </>
      )}
      {contact.frontEndInstructions?.vacationDates?.enabled ? (
        <>
          <Section data-cy="contact-details-fd-vacationDates">
            <Row label="Vacation">
              <Value>
                {moment(contact.frontEndInstructions.vacationDates.from).format(
                  'MMM DD, YYYY',
                )}{' '}
                -{' '}
              </Value>
              <Value>
                {moment(contact.frontEndInstructions.vacationDates.to).format(
                  'MMM DD, YYYY',
                )}
              </Value>
            </Row>
          </Section>
          <Divider />
        </>
      ) : null}
      {contact.frontEndInstructions?.copierCode && (
        <>
          <Section data-cy="contact-details-fd-copierCode">
            <Row label="Copier Code">
              <Value>{contact.frontEndInstructions.copierCode}</Value>
            </Row>
          </Section>
          <Divider />
        </>
      )}
      {contact.frontEndInstructions?.notes ? (
        <>
          <Section data-cy="contact-details-fd-notes">
            <Row label="Notes">
              <Value>
                {contact.frontEndInstructions.notes.substr(0, 200)}
                {contact.frontEndInstructions.notes.length > 200 ? '...' : ''}
              </Value>
            </Row>
          </Section>
          <Divider />
        </>
      ) : null}
    </StyledContainer>
  );
};

export default UserDetails;
